/**
 * Containment rules shared by every centered modal surface. (PANDA-1565)
 *
 * A modal used to be a content-sized box that was merely CAPPED and then allowed
 * to scroll as a whole (`max-h-[90vh] overflow-y-auto` on the popup). That keeps
 * the content on screen, but it scrolls the card's own chrome away with it: the
 * close button is positioned `absolute` INSIDE the scrolling element, so
 * scrolling down to reach the CTA pushes the ✕ off the top edge, and the
 * headline and plan names go with it. On a 1440x700 laptop the user could see
 * the way out or the thing to click — never both at once. Capping alone is why
 * this kept coming back.
 *
 * These classes fix the SHAPE rather than the height: the popup becomes a
 * bounded flex column that never scrolls itself, and exactly one child inside it
 * scrolls. Anything outside that child (header, ✕, sticky footer) therefore
 * stays pinned at every scroll position.
 *
 * `dvh` rather than `vh` is deliberate: on mobile Safari `vh` measures the
 * viewport as if the browser chrome were retracted, so a `90vh` card already
 * overflows the screen while the toolbars are showing — which is precisely the
 * state the screen is in when a modal opens.
 */

/**
 * The centered popup element. Bounds the modal to the viewport and makes it a
 * column; pair with `MODAL_SCROLL_BODY` on the one region that should scroll.
 *
 * `overflow-hidden` (not `auto`) is the point: the popup must NOT be the
 * scroller, or the pinned chrome scrolls with it again.
 */
export const MODAL_VIEWPORT_FRAME =
  "flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden"

/**
 * Same as `MODAL_VIEWPORT_FRAME` but only from `sm` up — for modals that render
 * as an edge-to-edge full-screen sheet on phones (where `inset-0` already bounds
 * them to the viewport) and as a centered card above it.
 */
export const MODAL_VIEWPORT_FRAME_SM =
  "sm:max-h-[calc(100dvh-2rem)] sm:flex-col sm:overflow-hidden"

/**
 * The single scrolling region inside a modal frame.
 *
 * `min-h-0` is load-bearing: a flex child defaults to `min-height:auto`, which
 * refuses to shrink below its content, so without it the body pushes the
 * overflow straight back out through the bottom of the card and nothing
 * scrolls. `overscroll-contain` stops a scroll that reaches the end from
 * chaining to the page behind the modal.
 */
export const MODAL_SCROLL_BODY =
  "custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain"
